import { type ChangeSet, computeUnpublishedFromOps, reconstructStateFromEvents, type State } from '@aidkitorg/typesheets/lib/collab';
import type { Root } from '@aidkitorg/types/lib/survey';
import type { Directory } from '@aidkitorg/aidkit/lib/directory';
import type { extractParams, extractReturnAsJson } from '@aidkitorg/types/lib/endpoints';

// Local twin of Util's safeParse: importing ./Util here would drag the whole
// app dependency graph into this deliberately headless module.
function safeParse(json: string, safeReturn: unknown) {
  try {
    return JSON.parse(json);
  } catch {
    return safeReturn;
  }
}

/** The call shape usePost returns for a given Directory route. */
export type PostTo<P extends keyof Directory> =
  (params: extractParams<Directory[P]>, signal?: AbortSignal) => Promise<extractReturnAsJson<Directory[P]>>;

export type CollabEventsResponse = Pick<
  extractReturnAsJson<Directory['/survey/get_collab_events']>,
  'events' | 'savedVersionURL' | 'parentVersionURL'
>;

export interface ResolvedCollabEvents {
  events: ChangeSet;
  unpublishedEvents: ChangeSet;
  /** Published ops at branch creation; empty for branches without a parent_ops_version. */
  parentOps: ChangeSet;
  parentState: State | undefined;
}

/**
 * Resolves the full event list from a get_collab_events response.
 * Uses parent ops + unpublished events when available; falls back to branch ops file + dynamo events.
 *
 * Ordering matters here: collabResp.events was scanned from Dynamo before this response
 * was built, and compaction writes the branch ops file BEFORE deleting consumed events
 * from Dynamo. Fetching the ops files strictly after receiving the response therefore
 * can at worst duplicate an event (op ids dedupe via LWW), never miss one. Do not
 * "optimize" by prefetching the ops files ahead of the get_collab_events call.
 */
export async function resolveCollabEvents(collabResp: CollabEventsResponse): Promise<ResolvedCollabEvents> {
  if (collabResp.parentVersionURL?.length > 0 && collabResp.savedVersionURL?.length > 0) {
    const [parentResponse, branchResponse] = await Promise.all([
      fetch(collabResp.parentVersionURL),
      fetch(collabResp.savedVersionURL),
    ]);
    if (!branchResponse.ok) throw new Error(`Failed to fetch ops file: ${branchResponse.status}`);
    // Fail loudly rather than falling through: without the parent file there is no way
    // to derive the unpublished list — falling back would silently render an empty
    // Changes tab.
    if (!parentResponse.ok) throw new Error(`Failed to fetch parent ops file: ${parentResponse.status}`);
    const parentParsed = safeParse(await parentResponse.text(), []);
    const branchParsed = safeParse(await branchResponse.text(), []);
    const parentOps: ChangeSet = Array.isArray(parentParsed) ? parentParsed : [];
    const branchOps: ChangeSet = Array.isArray(branchParsed) ? branchParsed : [];
    if (parentOps.length === 0) throw new Error('Parent ops file was empty or invalid');

    const [, parentState] = reconstructStateFromEvents(parentOps, { isInitialLoad: true });

    const pendingEvents = collabResp.events ?? [];
    return {
      // branch ops file + events pending compaction = full current state
      events: [...branchOps, ...pendingEvents],
      // Unpublished changes are derived from the ops files (the retired
      // unpublished_events table is no longer served by the backend)
      unpublishedEvents: computeUnpublishedFromOps(parentOps, branchOps, pendingEvents),
      parentOps,
      parentState,
    };
  }

  // Fallback for branches without parent_ops_version: branch ops file + dynamo events.
  // No live branches lack a parent (verified via ./cli findBranchesWithoutParentOps),
  // so there is nothing to show as unpublished here.
  let versionEvents: ChangeSet = [];
  if (collabResp.savedVersionURL?.length > 0) {
    const response = await fetch(collabResp.savedVersionURL);
    if (!response.ok) throw new Error(`Failed to fetch ops file: ${response.status}`);
    const parsed = safeParse(await response.text(), []);
    versionEvents = Array.isArray(parsed) ? parsed : [];
  }
  return {
    events: [...versionEvents, ...collabResp.events],
    unpublishedEvents: [],
    parentOps: [],
    parentState: undefined,
  };
}

/**
 * Light sibling of resolveCollabEvents for snapshot READS: the branch ops file
 * plus pending Dynamo events IS the full current state, so the parent ops file
 * (needed only to derive the unpublished-changes list) is never downloaded or
 * reconstructed. On large documents that halves the transfer and skips an
 * entire CRDT replay. The same ordering invariant applies: ops files are
 * fetched strictly AFTER the get_collab_events response.
 */
export async function resolveCurrentCollabEvents(collabResp: CollabEventsResponse): Promise<ChangeSet> {
  let versionEvents: ChangeSet = [];
  if (collabResp.savedVersionURL?.length > 0) {
    const response = await fetch(collabResp.savedVersionURL);
    if (!response.ok) throw new Error(`Failed to fetch ops file: ${response.status}`);
    const parsed = safeParse(await response.text(), []);
    versionEvents = Array.isArray(parsed) ? parsed : [];
  }
  return [...versionEvents, ...(collabResp.events ?? [])];
}

export interface RemoteSurveyPosts {
  getCollabEnabled: PostTo<'/survey/get_collab_enabled'>;
  getBranches: PostTo<'/survey/get_branches'>;
  getCollabEvents: PostTo<'/survey/get_collab_events'>;
  loadSurvey: PostTo<'/survey/load_survey'>;
}

export interface RemoteSurveyResult {
  survey: Root;
  surveyName: string;
  collabEnabled: boolean;
  /** Set only on the collab path — non-collab surveys have no branches. */
  branchName?: string;
  branchId?: number;
  /** Epoch ms when this snapshot was fetched; echoed in tool output so staleness is visible. */
  fetchedAt: number;
}

const REMOTE_SURVEY_TTL_MS = 30_000;
const remoteSurveyCache = new Map<string, { result: RemoteSurveyResult, expiresAt: number }>();

/** Test hook + escape hatch: a diverged or suspect snapshot should never be served again. */
export function evictRemoteSurvey(surveyName?: string) {
  if (surveyName === undefined) {
    remoteSurveyCache.clear();
    return;
  }
  Array.from(remoteSurveyCache.keys()).forEach(key => {
    if (key.startsWith(`${surveyName}|`)) remoteSurveyCache.delete(key);
  });
}

/**
 * Loads a snapshot of another survey document (entireprogram / adminpanel / audit),
 * optionally on a named branch. Collab surveys are reconstructed from their ops;
 * non-collab surveys fall back to the published document via load_survey.
 *
 * Branch resolution: an explicit `branchName` wins (and errors if absent);
 * without one, the target's dev (default) branch is read.
 *
 * Results are cached for 30s per (survey, branch) — snapshot semantics by design.
 * Errors (no such branch, audit permission, branch on a non-collab survey) throw
 * with a message meant to be surfaced verbatim as a tool result.
 */
export async function loadRemoteSurvey(
  posts: RemoteSurveyPosts,
  { name, branchName, now = Date.now() }: {
    name: string,
    branchName?: string,
    now?: number,
  },
): Promise<RemoteSurveyResult> {
  const cacheKey = `${name}|${branchName ?? ''}`;
  const cached = remoteSurveyCache.get(cacheKey);
  if (cached && cached.expiresAt > now) return cached.result;

  const collabEnabled = await posts.getCollabEnabled({ name });

  let result: RemoteSurveyResult;
  if (collabEnabled) {
    const branches = await posts.getBranches({ survey: name });
    const branch = branchName !== undefined
      ? branches.find(b => b.name === branchName)
      : branches.find(b => b.is_dev_branch);
    if (!branch) {
      const available = branches.map(b => b.name).join(', ') || '(none)';
      throw new Error(branchName !== undefined
        ? `Survey "${name}" has no branch named "${branchName}". Available branches: ${available}.`
        : `Survey "${name}" has no dev branch. Available branches: ${available}.`);
    }

    const collabResp = await posts.getCollabEvents({ name, branchId: branch.id });
    const events = await resolveCurrentCollabEvents(collabResp);
    const [root] = reconstructStateFromEvents(events, { isInitialLoad: true });
    result = {
      survey: root.read() as Root,
      surveyName: name,
      collabEnabled: true,
      branchName: branch.name,
      branchId: branch.id,
      fetchedAt: now,
    };
  } else {
    if (branchName !== undefined) {
      throw new Error(`Survey "${name}" does not have collaborative editing enabled, so it has no branches. Omit the branch parameter to read its published version.`);
    }
    const resp = await posts.loadSurvey({ name, purpose: 'distro AI cross-survey read' });
    result = {
      survey: resp.config as Root,
      surveyName: name,
      collabEnabled: false,
      fetchedAt: now,
    };
  }

  remoteSurveyCache.set(cacheKey, { result, expiresAt: now + REMOTE_SURVEY_TTL_MS });
  return result;
}
